import React, { useMemo } from 'react';
import { Select } from '@bedrock/components';
import { useControllableValue, useMemoizedFn } from 'ahooks';

import { DemandKpiValueDto } from '#services/api';
import useKPIList from '#features/KPITable/useKPIList';

export interface AuxiliaryKpiSelectProps {
  /** Form 注入：当前已选的辅助自定义指标（对象数组，符合后端提交结构） */
  value?: DemandKpiValueDto[];
  /** Form 注入 */
  onChange?: (value: DemandKpiValueDto[]) => void;
  /** 需求详情 id，用于 useKPIList 的模块级缓存键 */
  detailId?: string | number;
  /** 是否只读 */
  view?: boolean;
  /** 仅展示指定平台的 KPI（默认 DSPF） */
  platform?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * 需要从候选项中排除的 kpi_code 列表
   * 典型场景：辅助自定义指标需要过滤掉主要指标已选的 code，避免重复
   * 注意：仅影响下拉候选项，不会清除当前已选中的值
   */
  excludeCodes?: string[];
}

/**
 * 辅助自定义指标范围选择器
 *
 * 模仿 KPITable 的对接方式：通过 Form.Item + render 注入 value/onChange。
 * 组件内部：
 *   1) 用 useKPIList 拉取指标候选（DSPF 平台）；
 *   2) Select 层用 code 数组维护选中态；
 *   3) onChange 时把 code 数组映射成后端要求的 DemandKpiValueDto[] 结构
 *      （包含 kpi_code + 完整 kpi_schema + 默认字段）。
 */
const AuxiliaryKpiSelect: React.FC<AuxiliaryKpiSelectProps> = (props) => {
  const {
    detailId,
    view,
    platform = 'DSPF',
    placeholder = '请选择',
    disabled,
    excludeCodes,
  } = props;

  const [value, triggerChange] = useControllableValue<DemandKpiValueDto[]>(
    props,
    {
      defaultValue: [],
    },
  );


  // 编辑态才发请求；view 态用 value 里自带的 kpi_schema 兜底展示
  const kpiList = useKPIList(!view, detailId ?? '1');

  // 当前已选 code 数组（供 Select value 使用）
  const selectedCodes = useMemo(
    () =>
      (value ?? [])
        .map((item) => item?.kpi_code)
        .filter((code): code is string => !!code),
    [value],
  );

  // 需要从候选项中排除的 code 集合（主要指标已选 code）
  // 但为保证当前已选项的 label 能正常回显，不会从「已选项」中移除
  const excludeSet = useMemo(() => {
    return new Set(
      (excludeCodes ?? []).filter(
        (code): code is string => !!code && !selectedCodes.includes(code),
      ),
    );
  }, [excludeCodes, selectedCodes]);

  // Select options：
  //   1) 主路径：用拉取到的 KPIList 过滤 platform，并排除 excludeCodes；
  //   2) Fallback：KPIList 为空时，用表单值里的 kpi_schema 生成占位 option，
  //      保证多选 label 能正常回显。
  const options = useMemo(() => {
    const filtered = (kpiList ?? []).filter(
      (kpi) =>
        !!kpi?.code &&
        (platform ? kpi?.platform === platform : true) &&
        !excludeSet.has(kpi.code as string),
    );
    if (filtered.length) {
      return filtered.map((kpi) => ({
        name: kpi.name ?? (kpi.code as string),
        value: kpi.code as string,
      }));
    }
    return (value ?? [])
      .filter((item) => !!item?.kpi_code)
      .map((item) => ({
        name:
          (item.kpi_schema as any)?.name ?? (item.kpi_code as string),
        value: item.kpi_code as string,
      }));
  }, [kpiList, platform, value, excludeSet]);

  const handleChange = useMemoizedFn((codes: string[]) => {
    const nextCodes = Array.isArray(codes) ? codes : [];
    const nextValues: DemandKpiValueDto[] = nextCodes.map((code) => {
      // 优先沿用已存在的对象，保留用户可能已填的扩展字段
      const existing = (value ?? []).find((item) => item?.kpi_code === code);
      if (existing) return existing;
      // 新增：从 kpiList 中取出完整 kpi_schema
      const kpiSchema = (kpiList ?? []).find((kpi) => kpi?.code === code);
      return {
        kpi_code: code,
        kpi_schema: kpiSchema as any,
        custom: 0,
        accept_flag: false,
        show_for_out: false,
      } as DemandKpiValueDto;
    });
    triggerChange(nextValues);
  });

  return (
    <Select
      multiple
      type="label"
      placeholder={placeholder}
      options={options}
      value={selectedCodes}
      onChange={handleChange}
      disabled={disabled ?? view}
      showSearch
    />
  );
};

export default AuxiliaryKpiSelect;